<template>
  <div class="py-4">
    <BErrors :errors="errors" class="mb-3" />

    <div v-if="loaded">
      <form id="payment-form">
        <!-- Payment element will be mounted here -->
        <div id="payment-element" />

        <!-- Address element will be mounted here -->
        <div id="address-element" class="mt-4" />
      </form>

      <UButton class="mt-4" block size="lg" :loading="processing" :disabled="disabled || processing" @click="process">
        {{ confirmButtonTitle }}
      </UButton>
    </div>

    <div v-else class="text-center">
      <BSpinner label="Loading" class="bookapp-action-tint-color" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js';

const { $i18n, $sentry, $site } = useNuxtApp()

const stripeSupportedLocales = [
  'ar','bg','cs','da','de','el','en',
  'en-GB','es','es-419','et','fi','fil',
  'fr','fr-CA','he','hr','hu','id','it',
  'ja','ko','lt','lv','ms','mt','nb',
  'nl','pl','pt-BR','pt','ro','ru',
  'sk','sl','sv','th','tr','vi','zh',
  'zh-HK','zh-TW'
]

interface Props {
  label: string
  amount: number
  customer?: any
  confirmButtonTitle: string
  integration: any
  disabled: boolean
  action: Function
  callbackUrl: Function
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  amount: 0,
  customer: null,
  confirmButtonTitle: '',
  integration: null,
  disabled: false,
  action: () => {},
  callbackUrl: () => {}
})

const emit = defineEmits(['callback'])

const intent = ref()
const stripe = ref()
const element = ref()
const elements = ref()
const address = ref()
const errors = ref<string[]>([])
const processing = ref(false)
const loaded = ref(false)
const billingDetails = ref<any>({
  name: '',
  email: '',
  phone: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: ''
  }
})


const isSetupIntent = computed(() => {
  return intent.value?.object === 'setup_intent'
})

const isPaymentIntent = computed(() => {
  return intent.value?.object === 'payment_intent'
})

watch(
  () => $i18n.locale,
  () => load()
)


onMounted(() => {
    if (props.customer) {
    billingDetails.value.name = props.customer.displayName
    billingDetails.value.email = props.customer.email
    billingDetails.value.address = {}

    if (props.customer.address) {
      billingDetails.value.address.line1 = props.customer.address
    }
    if (props.customer.address2) {
      billingDetails.value.address.line2 = props.customer.address2
    }

    if (props.customer.suburb && props.customer.city) {
      billingDetails.value.address.city = props.customer.suburb + ', ' + props.customer.city
    } else if (props.customer.suburb) {
      billingDetails.value.address.city = props.customer.suburb
    } else if (props.customer.city) {
      billingDetails.value.address.city = props.customer.city
    }
    if (props.customer.region) {
      billingDetails.value.address.state = props.customer.region
    }
    if (props.customer.postcode) {
      billingDetails.value.address.postal_code = props.customer.postcode
    }
    if (props.customer.postcode) {
      billingDetails.value.address.postal_code = props.customer.postcode
    }

    if (props.customer.country) {
      billingDetails.value.address.country = props.customer.country
    } else {
      billingDetails.value.address.country = props.integration.data.country
    }
  }

  load()
})

    // Load stripe and mount elements
const load = async () => {
  try {
    const integration = props.integration

    const pk = props.integration.data.publishableKey
    const apiVersion = props.integration.data.apiVersion

    let locale = 'auto'

    if (stripeSupportedLocales.includes($i18n.locale)) {
      locale = $i18n.locale
    }

    const options = {
      // apiVersion,
      locale,
    }

    stripe.value = await loadStripe(pk, options)

    const intent = await setup()

    if (intent) {
      nextTick(() => {
        createStripeElements()

        mountPaymentElement()
        mountAddressElement()
      })
    } else {
      console.log('No intent!')
      errors.value = ['Sorry something went wrong while loading Stripe']
    }
  } catch (e) {
    console.log(e)
    errors.value = ['Sorry something went wrong while loading Stripe']
    // $sentry.captureException(e)
  }
}

const createStripeElements = () => {
  const options = {
    clientSecret: intent.value.client_secret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: $site.colors.action.background,
        colorBackground: $site.colors.content['background-2'],
        colorText: $site.colors.content.text
      }
    }
  }

  elements.value = stripe.value.elements(options)
}

const mountPaymentElement = () => {
  const options = {
    defaultValues: {
      billingDetails: billingDetails.value
    }
  }

  element.value = elements.value.create('payment', options)

  if (document.getElementById('payment-element')) {
    element.value.mount('#payment-element')
  }
}

const mountAddressElement = () => {
  const options = {
    defaultValues: {
      ...billingDetails.value
    },
    mode: 'shipping'
  }

  address.value = elements.value.create('address', options)

  if (document.getElementById('address-element')) {
    address.value.mount('#address-element')
  }
}

// Create payment/setup intent
const setup = async () => {
  try {
    const response = await props.action({ action: 'create-intent' })

    console.log(response)

    if (response.success) {
      intent.value = response.result
      loaded.value = true

      return intent.value
    } else {
      // $sentry.captureException(response.errors)
      errors.value = response.errors ?? ['Issue loading Stripe intent']
    }
  } catch (error) {
    console.log(error)
    errors.value.push($i18n.t('general.errorPayment'))
    // $sentry.captureException(error)
  }

  return null
}


const process = async () => {
  processing.value = true
  errors.value = []

  try {
    const return_url = await props.callbackUrl()

    // Payment intent when amount > 0
    if (isPaymentIntent.value) {
      const { paymentIntent, error } = await stripe.value.confirmPayment({
        elements: elements.value,
        confirmParams: { return_url },
        redirect: 'if_required'
      })

      if (error) {
        errors.value = [ error.message ]
      } else {
        emit('callback', { success: true, params: { paymentIntent: paymentIntent.id }})
      }
    }

    // Setup intent when amount <= 0 or null
    if (isSetupIntent.value) {
      const { setupIntent, error } = await stripe.value.confirmSetup({
        elements: elements.value,
        confirmParams: { return_url },
        redirect: 'if_required'
      })

      if (error) {
        errors.value = [ error.message ]
      } else {
        emit('callback', { success: true, params: { setupIntent: setupIntent.id }})
      }
    }
  } catch (e: any) {
    console.log(e)
    errors.value = [$i18n.t('general.errorPayment')]
    // $sentry.captureException(e?.message)
  }

  processing.value = false
}
</script>
